import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Building2, MapPin, Globe, Network, ChevronDown, ChevronUp, Plus, Check, Pause, X, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../api/client';

function useAssinaturas() {
  return useQuery({
    queryKey: ['assinaturas'],
    queryFn: async () => {
      const { data } = await api.get('/billing/assinaturas');
      return data;
    },
  });
}

function usePlanos() {
  return useQuery({
    queryKey: ['billing-planos'],
    queryFn: async () => {
      const { data } = await api.get('/billing/planos');
      return data;
    },
  });
}

function useEscolas() {
  return useQuery({
    queryKey: ['escolas-select'],
    queryFn: async () => {
      const { data } = await api.get('/escolas');
      return data;
    },
  });
}

function useEntidades() {
  return useQuery({
    queryKey: ['entidades-select'],
    queryFn: async () => {
      const [estados, municipios, redes] = await Promise.all([
        api.get('/estados').then(r => r.data),
        api.get('/municipios').then(r => r.data),
        api.get('/redes').then(r => r.data),
      ]);
      return { estados, municipios, redes };
    },
  });
}

const STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  ATIVA: { bg: 'rgba(16,185,129,.15)', color: '#10b981', label: 'Ativa' },
  TRIAL: { bg: 'rgba(59,130,246,.15)', color: '#3b82f6', label: 'Trial' },
  INADIMPLENTE: { bg: 'rgba(239,68,68,.15)', color: '#ef4444', label: 'Inadimplente' },
  SUSPENSA: { bg: 'rgba(245,158,11,.15)', color: '#f59e0b', label: 'Suspensa' },
  CANCELADA: { bg: 'rgba(107,114,128,.15)', color: '#6b7280', label: 'Cancelada' },
};

const ENTE_ICONS: Record<string, any> = { REDE: Network, ESTADO: Globe, MUNICIPIO: MapPin, ESCOLA_PRIVADA: Building2 };
const ENTE_LABELS: Record<string, string> = { REDE: 'Rede', ESTADO: 'Estado', MUNICIPIO: 'Município', ESCOLA_PRIVADA: 'Escola Privada' };

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', borderRadius: 8,
  border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, white)',
  color: 'var(--text-main, #111)', fontSize: '.88rem',
};
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '.76rem', fontWeight: 600, marginBottom: 6, color: 'var(--text-muted, #6b7280)' };

export function MasterAssinaturasPage() {
  const qc = useQueryClient();
  const { data: assinaturas, isLoading } = useAssinaturas();
  const { data: planos } = usePlanos();
  const { data: escolas } = useEscolas();
  const { data: entidades } = useEntidades();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Form state
  const [formTipo, setFormTipo] = useState('MUNICIPIO');
  const [formEnteId, setFormEnteId] = useState('');
  const [formPlanoId, setFormPlanoId] = useState('');
  const [formEscolas, setFormEscolas] = useState<string[]>([]);

  const total = assinaturas?.length || 0;
  const ativas = assinaturas?.filter((a: any) => a.status === 'ATIVA').length || 0;
  const trial = assinaturas?.filter((a: any) => a.status === 'TRIAL').length || 0;
  const escolasTotal = assinaturas?.reduce((acc: number, a: any) => acc + (a._count?.escolasCobertas || 0), 0) || 0;

  // Mutations
  const criarMut = useMutation({
    mutationFn: async () => {
      const body: any = { enteGestorTipo: formTipo, planoId: formPlanoId, quantidadeEscolas: formEscolas.length, inicioEm: new Date().toISOString().split('T')[0] };
      if (formTipo === 'ESTADO') body.estadoId = formEnteId;
      else if (formTipo === 'MUNICIPIO') body.municipioId = formEnteId;
      else if (formTipo === 'REDE') body.redeId = formEnteId;
      else body.escolaId = formEscolas[0] || formEnteId;

      const { data: ass } = await api.post('/billing/assinaturas', body);
      // Vincular escolas
      for (const eId of formEscolas) {
        await api.post(`/billing/assinaturas/${ass.id}/escolas`, { escolaId: eId });
      }
      return ass;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assinaturas'] }); qc.invalidateQueries({ queryKey: ['billing-metricas'] }); setShowModal(false); },
  });

  const ativarMut = useMutation({
    mutationFn: async (id: string) => { await api.patch(`/billing/assinaturas/${id}/ativar`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assinaturas'] }); qc.invalidateQueries({ queryKey: ['billing-metricas'] }); },
  });

  const suspenderMut = useMutation({
    mutationFn: async (id: string) => { await api.patch(`/billing/assinaturas/${id}/suspender`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assinaturas'] }); qc.invalidateQueries({ queryKey: ['billing-metricas'] }); },
  });

  const cancelarMut = useMutation({
    mutationFn: async (id: string) => { await api.patch(`/billing/assinaturas/${id}/cancelar`, { motivo: 'Cancelamento via painel master' }); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assinaturas'] }); qc.invalidateQueries({ queryKey: ['billing-metricas'] }); },
  });

  // Entidades para select
  const enteOptions = formTipo === 'ESTADO' ? (entidades?.estados || []) :
    formTipo === 'MUNICIPIO' ? (entidades?.municipios || []) :
    formTipo === 'REDE' ? (entidades?.redes || []) : (escolas || []);

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ HERO BANNER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #0c4a6e 0%, #1e3a5f 40%, #312e81 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(56,189,248,.1)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <ClipboardList size={18} style={{ color: '#38bdf8' }} />
                <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>Gestão Financeira</span>
              </div>
              <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Assinaturas</h1>
              <p style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', marginTop: 4 }}>Contratos ativos, trials e histórico de cobrança por escola</p>
            </div>
            <button
              onClick={() => { setFormTipo('MUNICIPIO'); setFormEnteId(''); setFormPlanoId(''); setFormEscolas([]); setShowModal(true); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
                background: '#38bdf8', color: '#0c4a6e', fontWeight: 700, fontSize: '.85rem', border: 'none', cursor: 'pointer',
              }}
            >
              <Plus size={16} /> Nova Assinatura
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, marginTop: 24 }}>
            {[
              { icon: ClipboardList, value: total, label: 'Total', color: '#38bdf8' },
              { icon: Building2, value: ativas, label: 'Ativas', color: '#10b981' },
              { icon: Globe, value: trial, label: 'Em Trial', color: '#a78bfa' },
              { icon: MapPin, value: escolasTotal, label: 'Escolas Cobertas', color: '#fbbf24' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${s.color}20`, color: s.color }}>
                  <s.icon size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '.75rem', color: 'rgba(255,255,255,.6)', fontWeight: 500 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ LISTA ═══ */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Carregando...</div>
      ) : !assinaturas || assinaturas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Nenhuma assinatura. Clique "Nova Assinatura" para criar.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {assinaturas.map((ass: any) => {
            const st = STATUS_COLORS[ass.status] || STATUS_COLORS.ATIVA;
            const EnteIcon = ENTE_ICONS[ass.enteGestorTipo] || Building2;
            const enteLabel = ENTE_LABELS[ass.enteGestorTipo] || ass.enteGestorTipo;
            const enteName = ass.rede?.nome || ass.estado?.nome || ass.municipio?.nome || ass.escola?.nome || '-';
            const isExpanded = expandedId === ass.id;
            const inicioFmt = new Date(ass.inicioEm).toLocaleDateString('pt-BR');
            const fimFmt = ass.fimEm ? new Date(ass.fimEm).toLocaleDateString('pt-BR') : 'Auto';
            const escolasCount = ass._count?.escolasCobertas || 0;

            return (
              <div key={ass.id} style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', overflow: 'hidden' }}>
                <div onClick={() => setExpandedId(isExpanded ? null : ass.id)} style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', borderBottom: isExpanded ? '1px solid var(--border-color)' : 'none' }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${st.color}15`, color: st.color, flexShrink: 0 }}>
                    <EnteIcon size={20} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--text-main)' }}>{enteName}</div>
                    <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{enteLabel} · {ass.plano?.nome || '-'} · {escolasCount} escola{escolasCount !== 1 ? 's' : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right', marginRight: 12 }}>
                    <div style={{ fontWeight: 800, fontSize: '1rem', color: '#7c3aed' }}>
                      {ass.plano ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ass.plano.precoBaseEscola * escolasCount) : '-'}
                    </div>
                    <div style={{ fontSize: '.65rem', color: 'var(--text-muted)' }}>/mês</div>
                  </div>
                  <span style={{ padding: '4px 12px', borderRadius: 8, fontSize: '.72rem', fontWeight: 700, background: st.bg, color: st.color, flexShrink: 0 }}>{st.label}</span>
                  <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 80 }}>
                    <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{inicioFmt}</div>
                    <div style={{ fontSize: '.65rem', color: 'var(--text-muted)', opacity: .7 }}>{fimFmt}</div>
                  </div>
                  <div style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</div>
                </div>

                {isExpanded && (
                  <div style={{ padding: '16px 20px', background: 'var(--bg-main, #f9fafb)' }}>
                    {/* Ações */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                      {ass.status !== 'ATIVA' && ass.status !== 'CANCELADA' && (
                        <button onClick={() => ativarMut.mutate(ass.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#dcfce7', color: '#166534', fontWeight: 600, fontSize: '.78rem', cursor: 'pointer' }}>
                          <Check size={14} /> Ativar
                        </button>
                      )}
                      {ass.status === 'ATIVA' && (
                        <button onClick={() => suspenderMut.mutate(ass.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '.78rem', cursor: 'pointer' }}>
                          <Pause size={14} /> Suspender
                        </button>
                      )}
                      {ass.status !== 'CANCELADA' && (
                        <button onClick={() => { if (confirm('Cancelar esta assinatura?')) cancelarMut.mutate(ass.id); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#fee2e2', color: '#991b1b', fontWeight: 600, fontSize: '.78rem', cursor: 'pointer' }}>
                          <X size={14} /> Cancelar
                        </button>
                      )}
                    </div>

                    <div style={{ fontSize: '.78rem', fontWeight: 700, marginBottom: 10, color: 'var(--text-main)' }}>Escolas Cobertas ({escolasCount})</div>
                    {ass.escolasCobertas && ass.escolasCobertas.length > 0 ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                        {ass.escolasCobertas.map((ec: any) => (
                          <div key={ec.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: 'var(--bg-card, white)', borderRadius: 10, border: '1px solid var(--border-color)' }}>
                            <Building2 size={14} style={{ color: '#6366f1', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--text-main)' }}>{ec.escola?.nome || ec.escolaId}</div>
                              <div style={{ fontSize: '.65rem', color: 'var(--text-muted)' }}>Desde {new Date(ec.incluidaEm).toLocaleDateString('pt-BR')}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>Nenhuma escola vinculada.</p>
                    )}
                    {ass.motivoCancelamento && (
                      <div style={{ marginTop: 12, fontSize: '.75rem', color: 'var(--text-muted)' }}>Motivo cancelamento: <strong>{ass.motivoCancelamento}</strong></div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ MODAL NOVA ASSINATURA ═══ */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={{ background: 'var(--bg-card, white)', borderRadius: 16, width: 520, maxHeight: '80vh', overflow: 'auto', padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0, color: 'var(--text-main)' }}>Nova Assinatura</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>

            {/* Tipo de ente */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Quem paga?</label>
              <select value={formTipo} onChange={(e) => { setFormTipo(e.target.value); setFormEnteId(''); }} style={inputStyle}>
                <option value="MUNICIPIO">Município</option>
                <option value="ESTADO">Estado</option>
                <option value="REDE">Rede</option>
                <option value="ESCOLA_PRIVADA">Escola Privada (direto)</option>
              </select>
            </div>

            {/* Ente gestor */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>{ENTE_LABELS[formTipo] || 'Entidade'}</label>
              <select value={formEnteId} onChange={(e) => setFormEnteId(e.target.value)} style={inputStyle}>
                <option value="">Selecione...</option>
                {enteOptions.map((ent: any) => (
                  <option key={ent.id} value={ent.id}>{ent.nome}{ent.sigla ? ` (${ent.sigla})` : ''}</option>
                ))}
              </select>
            </div>

            {/* Plano */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Plano Comercial</label>
              <select value={formPlanoId} onChange={(e) => setFormPlanoId(e.target.value)} style={inputStyle}>
                <option value="">Selecione o plano...</option>
                {(planos || []).map((p: any) => (
                  <option key={p.id} value={p.id}>{p.nome} — {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(p.precoBaseEscola)}/escola/mês</option>
                ))}
              </select>
            </div>

            {/* Escolas */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Escolas a vincular ({formEscolas.length} selecionadas)</label>
              <div style={{ maxHeight: 180, overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: 8, padding: 8 }}>
                {(escolas || []).map((esc: any) => (
                  <label key={esc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 6, fontSize: '.82rem', color: 'var(--text-main)' }}>
                    <input type="checkbox" checked={formEscolas.includes(esc.id)}
                      onChange={(e) => {
                        if (e.target.checked) setFormEscolas([...formEscolas, esc.id]);
                        else setFormEscolas(formEscolas.filter(id => id !== esc.id));
                      }}
                    />
                    {esc.nome}
                  </label>
                ))}
              </div>
            </div>

            {/* Resumo */}
            {formPlanoId && formEscolas.length > 0 && (() => {
              const pl = (planos || []).find((p: any) => p.id === formPlanoId);
              const totalMes = pl ? pl.precoBaseEscola * formEscolas.length : 0;
              return (
                <div style={{ background: 'var(--bg-main, #f3f4f6)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: 6 }}>Resumo</div>
                  <div style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>
                    {formEscolas.length} escola{formEscolas.length > 1 ? 's' : ''} × {pl ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pl.precoBaseEscola) : '?'} = <strong style={{ color: '#7c3aed', fontSize: '1rem' }}>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalMes)}/mês</strong>
                  </div>
                </div>
              );
            })()}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-main)', fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
              <button
                onClick={() => criarMut.mutate()}
                disabled={!formEnteId || !formPlanoId || formEscolas.length === 0 || criarMut.isPending}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: 'none', fontWeight: 700, cursor: 'pointer',
                  background: (!formEnteId || !formPlanoId || formEscolas.length === 0) ? '#d1d5db' : '#6366f1', color: 'white',
                }}
              >
                {criarMut.isPending ? 'Criando...' : 'Criar Assinatura'}
              </button>
            </div>

            {criarMut.isError && (
              <div style={{ marginTop: 12, padding: '8px 14px', borderRadius: 8, background: '#fee2e2', color: '#991b1b', fontSize: '.78rem' }}>
                Erro: {(criarMut.error as any)?.message || 'Falha ao criar'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
